import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  IconButton,
  Typography,
  Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { useAuth } from '../../../AuthContext';

const BranchEditModal = ({
  open,
  onClose,
  onSave,
  isSaving = false,
  branchData = null
}) => {
  const { companyId, userId } = useAuth();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  const [formData, setFormData] = useState({
    Id: '',
    Code: '',
    Name: '',
    AddressLine1: '',
    AddressLine2: '',
    Location: '',
    PinCode: '',
    ContactNumber1: '',
    ContactNumber2: '',
    ContactNumber3: '',
    Email: '',
    GSTNumber: '',
    PanNumber: '',
    PaymentBankId: '',
    SaleBillPrefix: '',
    RentBillPrefix: '',
    EstimateBillPrefix: '',
    CashOpeningBalance: '',
    CashOpeningDate: '',
    StockOpeningBalance: '',
    StockOpeningDate: '',
    IsGST: false
  });

  const [errors, setErrors] = useState({});

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 600);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Populate form data when branchData changes
  useEffect(() => {
    if (branchData && open) {
      setFormData({
        Id: branchData.Id || '',
        Code: branchData.Code || '',
        Name: branchData.Name || '',
        AddressLine1: branchData.AddressLine1 || '',
        AddressLine2: branchData.AddressLine2 || '',
        Location: branchData.Location || '',
        PinCode: branchData.PinCode || '',
        ContactNumber1: branchData.ContactNumber1 || '',
        ContactNumber2: branchData.ContactNumber2 || '',
        ContactNumber3: branchData.ContactNumber3 || '',
        Email: branchData.Email || '',
        GSTNumber: branchData.GSTNumber || '',
        PanNumber: branchData.PanNumber || '',
        PaymentBankId: branchData.PaymentBankId || '',
        SaleBillPrefix: branchData.SaleBillPrefix || '',
        RentBillPrefix: branchData.RentBillPrefix || '',
        EstimateBillPrefix: branchData.EstimateBillPrefix || '',
        CashOpeningBalance: branchData.CashOpeningBalance || '',
        CashOpeningDate: branchData.CashOpeningDate ? branchData.CashOpeningDate.split('T')[0] : '',
        StockOpeningBalance: branchData.StockOpeningBalance || '',
        StockOpeningDate: branchData.StockOpeningDate ? branchData.StockOpeningDate.split('T')[0] : '',
        IsGST: branchData.IsGST || false
      });
    }
  }, [branchData, open]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Validate contact numbers - only allow digits, spaces, +, -, and ()
    if ((name === 'ContactNumber1' || name === 'ContactNumber2' || name === 'ContactNumber3') && value) {
      const numericPattern = /^[0-9+\-\s()]*$/;
      if (!numericPattern.test(value)) {
        return;
      }
    }
    
    // Validate PinCode - only allow digits
    if (name === 'PinCode' && value) {
      const pinCodePattern = /^[0-9]*$/;
      if (!pinCodePattern.test(value)) {
        return;
      }
    }
    
    // Validate PAN Number - only allow uppercase letters and digits
    if (name === 'PanNumber' && value) {
      const panPattern = /^[A-Z0-9]*$/;
      if (!panPattern.test(value)) {
        return;
      }
    }
    
    // Validate GST Number - only allow uppercase letters and digits
    if (name === 'GSTNumber' && value) {
      const gstPattern = /^[0-9A-Z]*$/;
      if (!gstPattern.test(value)) {
        return;
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.Code.trim()) {
      newErrors.Code = 'Branch code is required';
    }

    if (!formData.Name.trim()) {
      newErrors.Name = 'Branch name is required';
    }

    if (!formData.AddressLine1.trim()) {
      newErrors.AddressLine1 = 'Address is required';
    }

    // Validate ContactNumber1 is required
    if (!formData.ContactNumber1.trim()) {
      newErrors.ContactNumber1 = 'Contact number is required';
    } else {
      const digitsOnly = formData.ContactNumber1.replace(/[^0-9]/g, '');
      if (digitsOnly.length < 10) {
        newErrors.ContactNumber1 = 'Contact number must have at least 10 digits';
      }
    }

    // Validate ContactNumber2 if provided
    if (formData.ContactNumber2.trim()) {
      const digitsOnly = formData.ContactNumber2.replace(/[^0-9]/g, '');
      if (digitsOnly.length < 10) {
        newErrors.ContactNumber2 = 'Contact number must have at least 10 digits';
      }
    }

    // Validate ContactNumber3 if provided
    if (formData.ContactNumber3.trim()) {
      const digitsOnly = formData.ContactNumber3.replace(/[^0-9]/g, '');
      if (digitsOnly.length < 10) {
        newErrors.ContactNumber3 = 'Contact number must have at least 10 digits';
      }
    }

    // Validate Bill Prefixes are required
    if (!formData.SaleBillPrefix.trim()) {
      newErrors.SaleBillPrefix = 'Sale bill prefix is required';
    }

    if (!formData.RentBillPrefix.trim()) {
      newErrors.RentBillPrefix = 'Rent bill prefix is required';
    }

    if (!formData.EstimateBillPrefix.trim()) {
      newErrors.EstimateBillPrefix = 'Estimate bill prefix is required';
    }

    // Validate Postal Code if provided
    if (formData.PinCode.trim()) {
      if (formData.PinCode.length < 4) {
        newErrors.PinCode = 'Postal code must be at least 4 digits';
      } else if (formData.PinCode.length > 10) {
        newErrors.PinCode = 'Postal code cannot exceed 10 digits';
      }
    }

    // Validate Email format if provided
    if (formData.Email.trim()) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(formData.Email)) {
        newErrors.Email = 'Invalid email format';
      }
    }

    // Validate PAN Number format if provided
    if (formData.PanNumber.trim()) {
      const panPattern = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
      if (!panPattern.test(formData.PanNumber)) {
        newErrors.PanNumber = 'Invalid PAN format. Must be 10 characters (e.g., ABCDE1234F)';
      }
    }

    // Validate GST Number format if provided
    if (formData.GSTNumber.trim()) {
      const gstPattern = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
      if (!gstPattern.test(formData.GSTNumber)) {
        newErrors.GSTNumber = 'Invalid GST format. Must be 15 characters with Z at 14th position (e.g., 22AAAAA0000A1Z5)';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSave(formData);
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  // Common input styles
  const inputStyle = { borderRadius: '6px', fontSize: '13px', height: '32px' };
  const labelStyle = { fontSize: '11px' };
  const helperStyle = { fontSize: '9px', margin: '1px 0 0 0' };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        style: {
          borderRadius: isMobile ? '0' : '12px',
          background: "linear-gradient(135deg, #0d766e 0%, #164e63 100%)",
          padding: '2px',
          margin: isMobile ? '0' : '8px'
        }
      }}
    >
      <Box style={{
        background: 'white',
        borderRadius: isMobile ? '0' : '10px',
        overflow: 'hidden',
        height: isMobile ? '100vh' : 'auto'
      }}>
        <DialogTitle style={{
          background: "linear-gradient(135deg, #0d766e 0%, #164e63 100%)",
          color: 'white',
          padding: '8px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 'auto'
        }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <EditIcon style={{ fontSize: '18px' }} />
            <Typography variant="h6" style={{ fontWeight: '600', fontSize: '14px' }}>
              Edit Branch
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            style={{ color: 'white', padding: '2px' }}
            size="small"
          >
            <CloseIcon style={{ fontSize: '18px' }} />
          </IconButton>
        </DialogTitle>

        <DialogContent
          className="branch-modal-content"
          style={{
            padding: '8px 12px',
            paddingBottom: '4px',
            maxHeight: isMobile ? 'calc(100vh - 120px)' : '70vh',
            overflowY: 'auto',
            overflowX: 'hidden'
          }}
        >
          <style>
            {`
              .branch-modal-content::-webkit-scrollbar {
                width: 0px;
                display: none;
              }
              .branch-modal-content {
                scrollbar-width: none;
                -ms-overflow-style: none;
              }
            `}
          </style>
          <Typography variant="subtitle2" style={{
            color: '#666',
            marginBottom: '6px',
            fontSize: '10px',
            lineHeight: '1.2'
          }}>
            Update branch information
          </Typography>

          <Grid container spacing={1}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" style={{
                fontWeight: '600',
                marginBottom: '3px',
                color: '#2d3748',
                fontSize: '12px'
              }}>
                Basic Information
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                name="Code"
                label="Branch Code *"
                value={formData.Code}
                onChange={handleChange}
                fullWidth
                size="small"
                required
                error={!!errors.Code}
                helperText={errors.Code}
                placeholder="BR001"
                inputProps={{ maxLength: 5 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
                FormHelperTextProps={{ style: helperStyle }}
              />
            </Grid>

            <Grid item xs={12} sm={8}>
              <TextField
                name="Name"
                label="Branch Name *"
                value={formData.Name}
                onChange={handleChange}
                fullWidth
                size="small"
                required
                error={!!errors.Name}
                helperText={errors.Name}
                placeholder="Enter branch name"
                inputProps={{ maxLength: 150 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
                FormHelperTextProps={{ style: helperStyle }}
              />
            </Grid>

            {/* Address Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" style={{
                fontWeight: '600',
                marginBottom: '3px',
                marginTop: '2px',
                color: '#2d3748',
                fontSize: '12px'
              }}>
                Address Details
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="AddressLine1"
                label="Address Line 1 *"
                value={formData.AddressLine1}
                onChange={handleChange}
                fullWidth
                size="small"
                required
                error={!!errors.AddressLine1}
                helperText={errors.AddressLine1}
                placeholder="Street address"
                inputProps={{ maxLength: 100 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
                FormHelperTextProps={{ style: helperStyle }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="AddressLine2"
                label="Address Line 2"
                value={formData.AddressLine2}
                onChange={handleChange}
                fullWidth
                size="small"
                placeholder="Apartment, suite, etc."
                inputProps={{ maxLength: 100 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="Location"
                label="Location"
                value={formData.Location}
                onChange={handleChange}
                fullWidth
                size="small"
                placeholder="City"
                inputProps={{ maxLength: 50 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="PinCode"
                label="Postal Code"
                value={formData.PinCode}
                onChange={handleChange}
                fullWidth
                size="small"
                error={!!errors.PinCode}
                helperText={errors.PinCode}
                placeholder="Postal Code"
                inputProps={{ maxLength: 10 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
                FormHelperTextProps={{ style: helperStyle }}
              />
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" style={{
                fontWeight: '600',
                marginBottom: '3px',
                marginTop: '2px',
                color: '#2d3748',
                fontSize: '12px'
              }}>
                Contact Details
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                name="ContactNumber1"
                label="Contact Number 1 *"
                value={formData.ContactNumber1}
                onChange={handleChange}
                fullWidth
                size="small"
                required
                error={!!errors.ContactNumber1}
                helperText={errors.ContactNumber1}
                placeholder="+91 XXXXX XXXXX"
                inputProps={{ maxLength: 15 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
                FormHelperTextProps={{ style: helperStyle }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                name="ContactNumber2"
                label="Contact Number 2"
                value={formData.ContactNumber2}
                onChange={handleChange}
                fullWidth
                size="small"
                error={!!errors.ContactNumber2}
                helperText={errors.ContactNumber2}
                placeholder="+91 XXXXX XXXXX"
                inputProps={{ maxLength: 15 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
                FormHelperTextProps={{ style: helperStyle }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                name="ContactNumber3"
                label="Contact Number 3"
                value={formData.ContactNumber3}
                onChange={handleChange}
                fullWidth
                size="small"
                error={!!errors.ContactNumber3}
                helperText={errors.ContactNumber3}
                placeholder="+91 XXXXX XXXXX"
                inputProps={{ maxLength: 15 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
                FormHelperTextProps={{ style: helperStyle }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="Email"
                label="Email Address"
                type="email"
                value={formData.Email}
                onChange={handleChange}
                fullWidth
                size="small"
                error={!!errors.Email}
                helperText={errors.Email}
                placeholder="branch@company.com"
                inputProps={{ maxLength: 25 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
                FormHelperTextProps={{ style: helperStyle }}
              />
            </Grid>

            {/* Tax & Financial Details */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" style={{
                fontWeight: '600',
                marginBottom: '3px',
                marginTop: '2px',
                color: '#2d3748',
                fontSize: '12px'
              }}>
                Tax & Financial Details
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="GSTNumber"
                label="GST Number"
                value={formData.GSTNumber}
                onChange={handleChange}
                fullWidth
                size="small"
                error={!!errors.GSTNumber}
                helperText={errors.GSTNumber}
                placeholder="22AAAAA0000A1Z5"
                inputProps={{ maxLength: 15 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
                FormHelperTextProps={{ style: helperStyle }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="PanNumber"
                label="PAN Number"
                value={formData.PanNumber}
                onChange={handleChange}
                fullWidth
                size="small"
                error={!!errors.PanNumber}
                helperText={errors.PanNumber}
                placeholder="ABCDE1234F"
                inputProps={{ maxLength: 10 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
                FormHelperTextProps={{ style: helperStyle }}
              />
            </Grid>

            {/* Bill Prefix Details */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" style={{
                fontWeight: '600',
                marginBottom: '3px',
                marginTop: '2px',
                color: '#2d3748',
                fontSize: '12px'
              }}>
                Bill Prefix Details
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                name="SaleBillPrefix"
                label="Sale Bill Prefix *"
                value={formData.SaleBillPrefix}
                onChange={handleChange}
                fullWidth
                size="small"
                required
                error={!!errors.SaleBillPrefix}
                helperText={errors.SaleBillPrefix}
                placeholder="SALE"
                inputProps={{ maxLength: 5 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
                FormHelperTextProps={{ style: helperStyle }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                name="RentBillPrefix"
                label="Rent Bill Prefix *"
                value={formData.RentBillPrefix}
                onChange={handleChange}
                fullWidth
                size="small"
                required
                error={!!errors.RentBillPrefix}
                helperText={errors.RentBillPrefix}
                placeholder="RENT"
                inputProps={{ maxLength: 5 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
                FormHelperTextProps={{ style: helperStyle }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                name="EstimateBillPrefix"
                label="Estimate Bill Prefix *"
                value={formData.EstimateBillPrefix}
                onChange={handleChange}
                fullWidth
                size="small"
                required
                error={!!errors.EstimateBillPrefix}
                helperText={errors.EstimateBillPrefix}
                placeholder="EST"
                inputProps={{ maxLength: 5 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
                FormHelperTextProps={{ style: helperStyle }}
              />
            </Grid>

            {/* Opening Balances */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" style={{
                fontWeight: '600',
                marginBottom: '3px',
                marginTop: '2px',
                color: '#2d3748',
                fontSize: '12px'
              }}>
                Opening Balances
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="CashOpeningBalance"
                label="Cash Opening Balance"
                type="number"
                value={formData.CashOpeningBalance}
                onChange={handleChange}
                fullWidth
                size="small"
                placeholder="0.00"
                inputProps={{ step: "0.01", min: "0" }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="CashOpeningDate"
                label="Cash Opening Date"
                type="date"
                value={formData.CashOpeningDate}
                onChange={handleChange}
                fullWidth
                size="small"
                InputLabelProps={{
                  shrink: true,
                  style: labelStyle
                }}
                InputProps={{ style: inputStyle }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="StockOpeningBalance"
                label="Stock Opening Balance"
                type="number"
                value={formData.StockOpeningBalance}
                onChange={handleChange}
                fullWidth
                size="small"
                placeholder="0.00"
                inputProps={{ step: "0.01", min: "0" }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="StockOpeningDate"
                label="Stock Opening Date"
                type="date"
                value={formData.StockOpeningDate}
                onChange={handleChange}
                fullWidth
                size="small"
                InputLabelProps={{
                  shrink: true,
                  style: labelStyle
                }}
                InputProps={{ style: inputStyle }}
              />
            </Grid>

            {/* Configuration */}
            <Grid item xs={12}>
              <Box style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 8px',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                backgroundColor: 'white',
                minHeight: '28px',
                marginTop: '4px'
              }}>
                <input
                  type="checkbox"
                  name="IsGST"
                  checked={formData.IsGST}
                  onChange={handleChange}
                  style={{
                    width: '12px',
                    height: '12px',
                    accentColor: "#0d766e"
                  }}
                />
                <Typography style={{
                  fontSize: '11px',
                  fontWeight: '500',
                  color: '#4a5568'
                }}>
                  GST Enabled
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions style={{
          padding: '8px 12px',
          background: '#f8f9fa',
          borderTop: '1px solid #e9ecef',
          gap: '6px',
          minHeight: 'auto'
        }}>
          <Button
            onClick={handleClose}
            disabled={isSaving}
            style={{
              color: '#6c757d',
              textTransform: 'none',
              fontWeight: '500',
              fontSize: '12px',
              padding: '4px 8px'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSaving}
            variant="contained"
            style={{
              background: "linear-gradient(135deg, #0d766e 0%, #164e63 100%)",
              color: 'white',
              textTransform: 'none',
              fontWeight: '600',
              borderRadius: '4px',
              padding: '4px 12px',
              fontSize: '12px'
            }}
          >
            {isSaving ? 'Updating...' : 'Update Branch'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default BranchEditModal;
